import { rankColors, type RegionCell } from "pages/cpiReports/regionMapOptions";

/**
 * The Overview's emissions map, reduced to arithmetic.
 *
 * Deliberately NOT deck.gl. The Overview is the landing page — every user, every
 * session — and its CO2 bar chart is already drawn by hand rather than with a
 * charting library. Following that costs about 4KB of inline SVG and no new
 * network requests, where embedding the real map would pull a 359KB chunk and
 * send every user's viewport to a tile host just for loading the home page.
 *
 * The full map is one click away and keeps the panning, routes and street map.
 */

export interface MiniRegion {
  id: string;
  name: string | null;
  /** SVG "x,y x,y" ready for a <polygon points> attribute. */
  points: string;
  fill: string;
  sharePct: number;
  tonnes: number;
}

export interface MiniRow {
  id: string;
  name: string | null;
  sharePct: number;
  tonnes: number;
  fill: string;
}

/**
 * Fallback frame when a tenant has no placeable pickups: western and central
 * Europe. Without it the projection has no extent to fit and the card renders an
 * empty box, which reads as a broken chart rather than as "no data yet".
 */
export const FALLBACK_BOUNDS = { minLat: 40, maxLat: 60, minLng: -10, maxLng: 20 };

export interface Projection {
  project(lat: number, lng: number): [number, number];
}

/** Web Mercator y, in degrees, so the thumbnail and the full map agree on shape. */
function mercatorY(lat: number): number {
  const clamped = Math.min(Math.max(lat, -85.05), 85.05);
  return (Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 180 / 2)) * 180) / Math.PI;
}

function rgbToCss([r, g, b]: [number, number, number]): string {
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Project every region into one SVG viewBox.
 *
 * Scaled by the TIGHTER of the two axes and then centred, so the footprint keeps
 * its true proportions. Fitting each axis independently would stretch Zeeland
 * into a shape no one would recognise, which defeats the point of using real
 * boundaries.
 */
export function fitProjection(
  points: [number, number][],
  width: number,
  height: number,
  padding = 8
): Projection {
  const lats = points.length ? points.map((p) => p[0]) : [FALLBACK_BOUNDS.minLat, FALLBACK_BOUNDS.maxLat];
  const lngs = points.length ? points.map((p) => p[1]) : [FALLBACK_BOUNDS.minLng, FALLBACK_BOUNDS.maxLng];
  const minX = Math.min(...lngs);
  const maxX = Math.max(...lngs);
  const minY = mercatorY(Math.min(...lats));
  const maxY = mercatorY(Math.max(...lats));

  const spanX = Math.max(maxX - minX, 1e-6);
  const spanY = Math.max(maxY - minY, 1e-6);
  const inner = { w: Math.max(width - padding * 2, 1), h: Math.max(height - padding * 2, 1) };
  const scale = Math.min(inner.w / spanX, inner.h / spanY);
  const offsetX = padding + (inner.w - spanX * scale) / 2;
  const offsetY = padding + (inner.h - spanY * scale) / 2;

  return {
    project(lat: number, lng: number): [number, number] {
      return [
        offsetX + (lng - minX) * scale,
        // SVG y grows downward; Mercator y grows upward.
        offsetY + (maxY - mercatorY(lat)) * scale,
      ];
    },
  };
}

/** Country outlines, drawn behind the data so the frame is never blank. */
export function projectOutline(
  features: { geometry: { type: string; coordinates: unknown } }[],
  proj: Projection
): string[] {
  const out: string[] = [];
  for (const f of features) {
    const polys =
      f.geometry.type === "Polygon"
        ? [f.geometry.coordinates as number[][][]]
        : (f.geometry.coordinates as number[][][][]);
    for (const poly of polys) {
      // Exterior ring only: a thumbnail cannot show a hole, and shipping every
      // ring of 53 countries would cost more than the context is worth.
      const ring = poly[0];
      if (!ring || ring.length < 3) continue;
      out.push(
        ring
          .map(([lng, lat]) => {
            const [x, y] = proj.project(lat, lng);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ")
      );
    }
  }
  return out;
}

/**
 * Project every region into one SVG viewBox.
 *
 * Scaled by the TIGHTER of the two axes and then centred, so the footprint keeps
 * its true proportions. Fitting each axis independently would stretch Zeeland
 * into a shape no one would recognise, which defeats the point of using real
 * boundaries.
 */
export function projectRegions(
  cells: RegionCell[],
  totalKg: number,
  proj: Projection
): MiniRegion[] {
  const drawable = cells.filter((c) => (c.boundary?.length ?? 0) > 2);
  if (drawable.length === 0) return [];

  const colors = rankColors(drawable);

  return drawable.map((c) => {
    const points = (c.boundary ?? [])
      .map(([lat, lng]) => {
        const [x, y] = proj.project(lat, lng);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");

    return {
      id: c.h3,
      name: c.label,
      points,
      fill: rgbToCss(colors.get(c.h3) ?? [200, 200, 200]),
      sharePct: totalKg > 0 ? (c.emissionWtwKg / totalKg) * 100 : 0,
      tonnes: c.emissionWtwKg / 1000,
    };
  });
}

/**
 * The companion list.
 *
 * It exists because a thumbnail cannot be read precisely: shading says which
 * region is heaviest but never by how much, and at this size there is no room
 * for labels on the map itself. Naming the biggest few answers "which regions"
 * without asking anyone to decode a colour.
 */
export function topRegions(
  cells: RegionCell[],
  totalKg: number,
  limit = 5
): MiniRow[] {
  const colors = rankColors(cells);
  return [...cells]
    .sort((a, b) => b.emissionWtwKg - a.emissionWtwKg)
    .slice(0, limit)
    .map((c) => ({
      id: c.h3,
      name: c.label,
      sharePct: totalKg > 0 ? (c.emissionWtwKg / totalKg) * 100 : 0,
      tonnes: c.emissionWtwKg / 1000,
      fill: rgbToCss(colors.get(c.h3) ?? [200, 200, 200]),
    }));
}

/** Share as a percentage, one unit throughout — same rule as the full map. */
export function formatSharePct(share: number): string {
  if (share >= 10) return `${share.toFixed(0)}%`;
  if (share >= 0.1) return `${share.toFixed(1)}%`;
  if (share > 0) return "<0.1%";
  return "0%";
}

/** Tonnes, always, so two regions can be compared without converting. */
export function formatTonnes(t: number): string {
  if (t >= 100) return `${Math.round(t).toLocaleString()} t`;
  if (t >= 10) return `${t.toFixed(0)} t`;
  if (t >= 1) return `${t.toFixed(1)} t`;
  if (t > 0) return `${t.toFixed(2)} t`;
  return "0 t";
}
